import React from 'react';
import {Link} from 'react-router-dom';
import {DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown} from 'reactstrap';
import {ListConfigContext} from '@src/contexts/ListConfigContext';
import Translate from '@src/components/Translate/Translate';
import ErrorRepresentation from '@src/components/ErrorRepresentation/ErrorRepresentation';
import {
    ListConfigNamedColumn,
    ListItemControlsAvailableActions,
    TableBodyControlsTDPropsProvider,
    TableBodyProps,
    TableBodyState,
    TableBodyTDPropsProvider
} from '@src/@types/components/List';
import {IListColumnsFactory} from '@src/@interfaces/components/List/IListColumnsFactory';
import ListColumnsFactory from './columns/ListColumnsFactory';
import RenderIf from '@src/components/RenderIf/RenderIf';
import {IListItemControl} from '@src/@interfaces/components/List/IListItemControl';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import trans from '@src/helpers/trans';
import app from '@src/helpers/app';
import handleError from '@src/helpers/handleError';
import classnames from '@src/helpers/classnames';
import {IListItemLinkControl} from '@src/@interfaces/components/List/IListItemLinkControl';
import getUri from '@src/helpers/getUri';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {ConfirmableButtonProps} from '@src/@types/ConfirmableButton';
import NotificationException from '@src/exceptions/NotificationException';

type defaultItemControlsConfigs<M = any, T = IListItemControl | IListItemLinkControl> =
        Record<ListItemControlsAvailableActions, T | ((record: M) => T)>;

function DropdownItemWithInnerRefProp(props: any & {
    tag: string | React.ComponentType,
    innerRef: React.ForwardedRef<any>
}) {
    const {children, innerRef, ...rest} = props;

    return <div ref={innerRef}><DropdownItem {...rest}>{children}</DropdownItem></div>;
}

const emptyObject = {};
const tabIndex0Props = {tabIndex: 0};

export default class TableBody<Model extends IEmptyModel = IEmptyModel> extends React.Component<TableBodyProps<Model>, TableBodyState> {
    static contextType = ListConfigContext;
    context!: React.ContextType<typeof ListConfigContext>;

    state: TableBodyState = {
        recordsErrors: {},
        recordsMessages: {},
    };

    protected dynamicCellContentRenderer: TableBodyProps<Model>['renderCellContent'] | undefined = undefined;

    protected columnsFactoryInstance: IListColumnsFactory | undefined = undefined;

    protected defaultItemControlsConfigs: defaultItemControlsConfigs<Model> = {
        delete: () => ({
            type: 'delete',
            label: 'List.controls.delete',
            icon: `fas ${this.isRecordsDeletingIsIrreversible() ? 'fa-times' : 'fa-trash'}`,
            className: 'text-danger',
        }),
        restore: {
            type: 'restore',
            label: 'List.controls.restore',
            icon: 'fas fa-trash-arrow-up',
            className: 'text-success',
        },
        edit: record => {
            let to = '';
            if (this.context) {
                const {controllerName, config} = this.context;

                to = app('config-maker')
                        .makeListConfig(controllerName, config)
                        .getUrlOfModelShowPage(+record['id' as keyof Model]);
            }

            return {
                type: 'link',
                label: 'List.controls.edit',
                icon: 'fas fa-pencil',
                to,
            };
        },
    };

    constructor(props: TableBodyProps<Model>) {
        super(props);

        this.renderNoRecordsMessage = this.renderNoRecordsMessage.bind(this);
        this.renderColumns = this.renderColumns.bind(this);
    }

    render() {
        const {nowLoading, isPaused, error, records, tbodyProps} = this.props;

        let content: any;
        if (isPaused) {
            content = this.renderIsOfflineMessage();
        } else if (nowLoading) {
            content = this.renderLoader();
        } else if (error) {
            content = this.renderErrorRepresentation(error);
        } else {
            content = this.renderContent(records);
        }

        return <tbody {...(tbodyProps ?? emptyObject)}>{content}</tbody>;
    }

    protected renderContent(records?: any[]) {
        if (records && records.length > 0) {
            return this.renderRows(records);
        }

        return this.renderNoRecordsMessage();
    }

    protected renderLoader() {
        return <tr>
            <Translate
                    tag="td"
                    colSpan={this.context?.columnsNumber || 1}
                    className="text-center text-muted py-5"
                    message="List.messages.now-loading"
            />
        </tr>;
    }

    protected renderIsOfflineMessage() {
        return <tr>
            <Translate
                    tag="td"
                    colSpan={this.context?.columnsNumber || 1}
                    className="text-center text-muted py-5"
                    message="List.messages.is-offline"
            />
        </tr>;
    }

    protected renderErrorRepresentation(error: Error) {
        return <tr>
            <td colSpan={this.context?.columnsNumber || 1}>
                <ErrorRepresentation error={error} outsideContainer={true}/>
            </td>
        </tr>;
    }

    protected renderNoRecordsMessage() {
        return <tr>
            <Translate
                    tag="td"
                    colSpan={this.context?.columnsNumber || 1}
                    className="text-center text-muted py-5"
                    message={this.context?.config.noRecordsMessage ?? '@common-phrases.no-records-found'}
            />
        </tr>;
    }

    protected renderRows(records?: Model[]) {
        if (!records || !records.length) {
            return null;
        }

        return records.map(this.renderColumns);
    }

    protected renderColumns(record: Model | undefined, rowIndex: number) {
        type RowRendererParams = Parameters<Required<TableBodyProps<Model>>['renderRow']>;

        const context = this.context;
        const columns = this.props.columns ?? context?.columns;
        if (!context || !columns) {
            return null;
        }

        const recordId: string | null = record ? String(record['id' as keyof Model]) : null;
        const recordErrors = recordId ? this.state.recordsErrors[recordId] ?? [] : [];
        const recordMessages = recordId ? this.state.recordsMessages[recordId] ?? [] : [];
        const propsProvider = typeof this.props.getTRProps === 'function' ?
                this.props.getTRProps :
                (() => this.props?.getTRProps ?? emptyObject);

        const CellBeforeColumns = this.props.cellBeforeColumnsComponent;

        const baseRowContentRenderer = () => <>
            {CellBeforeColumns ? <CellBeforeColumns model={record} rowIndex={rowIndex}/> : null}

            {columns.map((column, cellIndex) => {
                return this.renderColumn(column, record, rowIndex, cellIndex);
            })}

            {this.renderControlsColumn(record, rowIndex)}
        </>;

        const rowContentRenderer = this.props.renderRowContent ?? baseRowContentRenderer;

        const baseRowRenderer: RowRendererParams[1] = (extraProps, contentRenderer) =>
                <tr
                        onMouseDown={e => this.onMouseDownDetermineMiddleButton(e, record, rowIndex)}
                        onClick={e => this.onMouseClick(e, record, rowIndex)}
                        {...(this.props.canInteractWithRows ? tabIndex0Props : emptyObject)}
                        {...propsProvider(record, rowIndex)}
                        {...(extraProps ?? emptyObject)}
                >
                    {(contentRenderer ?? rowContentRenderer)({
                        recordId,
                        rowIndex,
                        cellBeforeColumnsComponent: CellBeforeColumns,
                        context,
                        renderControlsColumn: () => this.renderControlsColumn(record, rowIndex),
                        renderColumns: (contentRenderer) => {
                            return this.withDynamicCellContentRenderer(contentRenderer, () => {
                                return columns.map((column, cellIndex) => {
                                    return this.renderColumn(column, record, rowIndex, cellIndex);
                                });
                            });
                        },
                        renderColumn: (column, record, rowIndex, cellIndex, contentRenderer) => {
                            return this.withDynamicCellContentRenderer(contentRenderer, () => {
                                return this.renderColumn(column, record, rowIndex, cellIndex);
                            });
                        },
                    }, baseRowContentRenderer)}
                </tr>;

        const keyProvider = this.props.getTRKeyValue ?? (
                () => `${context.controllerName}-model-list-${record?.id}-${rowIndex}-row`
        );

        return <React.Fragment key={keyProvider(record, rowIndex)}>
            {this.props.renderRow ? this.props.renderRow({
                recordId,
                rowIndex,
                context,
                model: record,
                onMouseDown: e => this.onMouseDownDetermineMiddleButton(e, record, rowIndex),
                onClick: e => this.onMouseClick(e, record, rowIndex),
                ...(this.props.canInteractWithRows ? tabIndex0Props : emptyObject),
                ...propsProvider(record, rowIndex),
            }, baseRowRenderer, baseRowContentRenderer) : baseRowRenderer()}

            {[recordErrors, recordMessages].map((messages, index) => {
                if (!messages.length) {
                    return null;
                }

                const id = String(recordId);
                const isError = !index;

                return <tr key={`record-${recordId}-bug-of-message-${index}`}>
                    <td colSpan={this.context?.columnsNumber} className={classnames({
                        'models-list__content-table__row-error': isError,
                        'models-list__content-table__row-message': !isError,
                    })}>
                        {messages.map((message, messageIndex) => {
                            return <div key={`record-${recordId}-${messageIndex}`}>
                                <Translate message={message} tag="div"/>
                                <button
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => {
                                            this.setState(state => {
                                                const containerName = isError ? 'recordsErrors' : 'recordsMessages';

                                                const list = state[containerName][id];
                                                list.splice(messageIndex, 1);

                                                return {
                                                    ...state,
                                                    [containerName]: {
                                                        ...state[containerName],
                                                        [id]: [...list],
                                                    }
                                                };
                                            });
                                        }}
                                />
                            </div>;
                        })}
                    </td>
                </tr>;
            })}
        </React.Fragment>;
    }

    protected renderColumn(column: ListConfigNamedColumn, record: Model | undefined, rowIndex: number, cellIndex: number) {
        const context = this.context;
        if (!record || !context) {
            return null;
        }

        const Component = this.factory.getComponent(column);
        const propsProvider: TableBodyTDPropsProvider = typeof this.props.getTDProps === 'function' ?
                this.props.getTDProps :
                (() => this.props?.getTDProps ?? emptyObject);

        const baseRenderer = () => <Component
                config={column}
                listConfig={context.config}
                model={record}
                rowIndex={rowIndex}
                cellIndex={cellIndex}
                controllerName={context.controllerName}
        />;

        const renderer = this.dynamicCellContentRenderer ?? this.props.renderCellContent ?? baseRenderer;

        return <td
                key={`${context.controllerName}-model-list-${rowIndex}-${cellIndex}-column`}
                {...propsProvider(column, record, rowIndex, cellIndex)}
        >
            {renderer({
                config: column,
                listConfig: context.config,
                model: record,
                rowIndex: rowIndex,
                cellIndex: cellIndex,
                controllerName: context.controllerName,
            }, baseRenderer)}
        </td>;
    }

    protected renderControlsColumn(record: Model | undefined, rowIndex: number) {
        if (!record || !this.context || !this.context.config.itemControls && !this.props.renderControls) {
            return <td/>;
        }

        let content: React.ReactNode = null;

        if (this.props.renderControls) {
            type TParams = Parameters<NonNullable<(typeof this.props)['renderControls']>>;
            const baseOnClick: TParams[2] = body => this.onControlCLick(record, false, body);
            const baseRender: TParams[3] = () => this.renderBaseItemControls(record, rowIndex);

            content = this.props.renderControls(record, rowIndex, baseOnClick, baseRender);
        } else if (this.context.config.itemControls) {
            content = this.renderBaseItemControls(record, rowIndex);
        }

        const propsProvider: TableBodyControlsTDPropsProvider<Model> =
                typeof this.props.getControlsTDProps === 'function' ?
                        this.props.getControlsTDProps :
                        (() => this.props?.getControlsTDProps ?? emptyObject);

        const tdProps = propsProvider(record, rowIndex);

        return <td
                {...tdProps}
                key={`${this.context.controllerName}-model-list-${rowIndex}-controls-column`}
                className={classnames('models-list__content-table__controls-column', tdProps.className)}
        >
            {content}
        </td>;
    }

    protected renderBaseItemControls(record: Model | undefined, rowIndex: number) {
        if (!record || !this.context || !this.context.config.itemControls) {
            return null;
        }

        const controls = this.context.config.itemControls;
        const controlsTypes: string[] = typeof controls.at(0) === 'string' ?
                controls :
                Object.values(controls).map(c => c.type);

        const isRecordMarkedAsDeleted = this.isRecordMarkedAsDelete(record);
        const isDefaultSetOfButtons = (
                controlsTypes.every(t => ['delete', 'restore'].includes(t))
        );

        if (isDefaultSetOfButtons) {
            return this.getExpandedItemControlsConfigs(record).map(config => {
                const isDeleteButton = config.type === 'delete';
                if (isDeleteButton === isRecordMarkedAsDeleted) {
                    return null;
                }

                return <React.Fragment key={`item-${rowIndex}-${+isRecordMarkedAsDeleted}-${config.type}-control`}>
                    {
                        isDeleteButton ?
                                this.renderDeleteButton(config, record) :
                                this.renderRestoreButton(config, record)
                    }
                </React.Fragment>;
            });
        }

        const controlToRender = this.getExpandedItemControlsConfigs(record).filter(
                c => (c.type === 'delete') !== isRecordMarkedAsDeleted
        );
        if (!controlToRender.length) {
            return null;
        }

        return <UncontrolledDropdown onClick={e => e.stopPropagation()}>
            <DropdownToggle color="info" size="sm" caret>
                <i className="fas fa-ellipsis-vertical"/>
            </DropdownToggle>

            <DropdownMenu>
                {controlToRender.map((config, controlIndex) => {
                    const isConfirmableButton = !('to' in config);
                    const isDeleteButton = config.type === 'delete';
                    if (isConfirmableButton && isDeleteButton === isRecordMarkedAsDeleted) {
                        return null;
                    }

                    const key = `item-${record['id' as keyof Model]}-${+isRecordMarkedAsDeleted}-${controlIndex}-control`;

                    if (isConfirmableButton) {
                        return <React.Fragment key={key}>
                            {this.renderConfirmableButton(config, {
                                buttonElement: DropdownItemWithInnerRefProp,
                                className: config.className,
                                displayLabelWithIcon: true,
                                toggle: false,
                            }, () => {
                                this.onControlCLick(record, !isDeleteButton).then();
                            })}
                        </React.Fragment>;
                    }

                    return <DropdownItem
                            key={key}
                            className={config.className}
                            tag={Link}
                            to={getUri(config.to)}
                    >
                        {config.icon ? <i className={classnames({
                            [config.icon]: true,
                            ' me-1': config.icon && config.label,
                        })}/> : null}
                        {config.label ? <Translate message={config.label}/> : null}
                    </DropdownItem>;
                })}
            </DropdownMenu>
        </UncontrolledDropdown>;
    }

    protected renderDeleteButton(buttonConfig: IListItemControl, record: Model | undefined) {
        if (!record) {
            return null;
        }

        return this.renderConfirmableButton(buttonConfig, {
            color: 'danger',
            popoverTitle: 'List.actions.delete.popover-title',
            popoverBody: this.isRecordsDeletingIsIrreversible() ? <Translate
                    message="List.actions.delete.is-irreversible-action"
                    className="alert alert-danger p-2 mb-2"
            /> : null,
            confirmButtonLabel: 'List.actions.delete.confirm-button-label',
            cancelButtonLabel: 'List.actions.delete.cancel-button-label',
        }, () => {
            this.onControlCLick(record, false).then();
        });
    }

    protected renderRestoreButton(buttonConfig: IListItemControl, record: Model | undefined) {
        if (!record) {
            return null;
        }

        return this.renderConfirmableButton(buttonConfig, {
            color: 'success',
            popoverTitle: 'List.actions.restore.popover-title',
            confirmButtonLabel: 'List.actions.restore.confirm-button-label',
            cancelButtonLabel: 'List.actions.restore.cancel-button-label',
        }, () => {
            this.onControlCLick(record, true).then();
        });
    }

    protected renderConfirmableButton(
            buttonConfig: IListItemControl,
            buttonProps: Omit<Partial<ConfirmableButtonProps>, 'onClick'> & {
                displayLabelWithIcon?: boolean,
            },
            onClick: React.MouseEventHandler
    ) {
        const {displayLabelWithIcon, ...props} = buttonProps;

        return <ConfirmableButton
                size="sm"
                title={trans(buttonConfig.label)}
                {...props}
                onClick={onClick}
        >
            <RenderIf
                    expression={displayLabelWithIcon}
                    render={() => <>
                        {buttonConfig.icon ? <i className={classnames({
                            [buttonConfig.icon]: true,
                            ' me-1': buttonConfig.icon && buttonConfig.label,
                        })}/> : null}
                        {buttonConfig.label ? <Translate message={buttonConfig.label}/> : null}
                    </>}
                    renderElse={() => {
                        if (buttonConfig.icon) {
                            return <i className={buttonConfig.icon}/>;
                        }

                        return <Translate message={buttonConfig.label}/>;
                    }}
            />
        </ConfirmableButton>;
    }

    protected sendDeleteOrRestoreRequest(
            record: Model,
            isRestoreRequest: boolean,
            additionalRequestData: Record<string, any> = {}
    ): Promise<boolean> {
        const routeName = 'abstract-models.' + (isRestoreRequest ? 'restore' : 'delete');

        const id = String(record['id' as keyof Model]);

        return app('http').route(routeName, {id}, {
            body: {
                'controller-name': this.context?.controllerName,
                ...additionalRequestData,
            },
            headers: {'Content-Type': 'multipart/form-data'},
        }).then(() => {
            this.props.refreshRecords();

            return true;
        }).catch(err => {
            const isMessage = err instanceof NotificationException;

            this.setState(state => {
                return {
                    ...state,
                    [isMessage ? 'recordsMessages' : 'recordsErrors']: {
                        ...state[isMessage ? 'recordsMessages' : 'recordsErrors'],
                        [id]: [
                            ...(state[isMessage ? 'recordsMessages' : 'recordsErrors'][id] ?? []),
                            err.message
                        ],
                    }
                };
            });

            handleError(err);

            return Promise.resolve(isMessage);
        });
    }

    protected getExpandedItemControlsConfigs(record: Model) {
        const controls = this.context?.config.itemControls ?? [];

        return controls.map(item => {
            if (typeof item === 'object') {
                return item;
            }

            const config = this.defaultItemControlsConfigs[item];
            if (typeof config === 'function') {
                return config(record);
            }

            return config;
        });
    }

    protected isRecordMarkedAsDelete(record: Model | undefined): boolean {
        const deletedAtColumnName = this.context?.config.deletedAtColumnName;
        if (!record || !deletedAtColumnName) {
            return false;
        }

        return (
                deletedAtColumnName in record &&
                record[deletedAtColumnName as keyof typeof record] != null
        );
    }

    protected isRecordsDeletingIsIrreversible(): boolean {
        return Boolean(this.context) && this.context?.config.deletedAtColumnName == null;
    }

    protected onMouseDownDetermineMiddleButton(e: React.MouseEvent, model: Model | undefined, rowIndex: number): void {
        if (e.button === 1 && this.props.onMiddleClick) {
            this.props.onMiddleClick(e, model, rowIndex);
        } else if (e.button === 2 && this.props.onRightClick) {
            this.props.onRightClick(e, model, rowIndex);
        }
    }

    protected onMouseClick(e: React.MouseEvent, model: Model | undefined, rowIndex: number): void {
        if (this.props.onLeftClick) {
            this.props.onLeftClick(e, model, rowIndex);
        }
    }

    protected onControlCLick(record: Model, isRestoreRequest: boolean, requestBody: Record<string, any> = {}) {
        return this.sendDeleteOrRestoreRequest(record, isRestoreRequest, requestBody);
    }

    protected withDynamicCellContentRenderer<T>(
            renderer: TableBodyProps<Model>['renderCellContent'] | undefined,
            callable: () => T
    ): T {
        const was = this.dynamicCellContentRenderer;
        this.dynamicCellContentRenderer = renderer;

        try {
            return callable();
        } finally {
            this.dynamicCellContentRenderer = was;
        }
    }

    protected get factory(): IListColumnsFactory {
        if (!this.columnsFactoryInstance) {
            const factory = this.context?.columnsFactory ?? new ListColumnsFactory();
            const customComponentsAccessor = this.context?.getCustomColumnComponent;
            if (customComponentsAccessor) {
                factory.setCustomComponentAccessor(customComponentsAccessor);
            }

            this.columnsFactoryInstance = factory;
        }

        return this.columnsFactoryInstance;
    }
}
